import React, { useEffect, useRef, useState } from 'react';
import {
    getAiScore,
    getDisplayedScore,
    parseScoreInput,
    readScoreOverride,
    SCORE_INPUT_ERROR_MESSAGE,
} from './scoreOverride';

/**
 * 推薦カードのマッチ度 (DP-M-004) と、その数字の書き換え (IN-M-010・BTN-M-010〜013・DP-M-014)。
 * 画面レイアウト_SCR-M-001.md §4.4・§6.2「マッチ度の数字の書き換え」、Issue #1435。
 *
 * 出す値は、手で直した値があればその値、無ければ AI の値。「マッチ度を表示する」(IN-M-009) を
 * 外している間は、何も出さない (編集もできない)。
 *
 * 編集中かどうか・保存中かどうか・保存の失敗の文言は、呼び出し元 (useScoreAdjustments) が持つ
 * (編集できるカードを同時に 1 枚にし、保存を重ねて送らないため)。入力欄の文字と入力の誤りは本部品が持つ。
 *
 * @param {object} props
 * @param {object} props.item 推薦の要素 (score・score_override)
 * @param {boolean} props.scoreHidden 非表示の設定か
 * @param {boolean} props.editing このカードを編集中か
 * @param {boolean} props.saving このカードのマッチ度を保存中か
 * @param {boolean} props.locked 編集・AI の値に戻すを押せないか (ほかのカードを編集中・マッチ度の保存中)
 * @param {string|null} props.errorMessage このカードの保存の失敗の文言
 * @param {() => void} props.onStartEdit
 * @param {() => void} props.onCancelEdit
 * @param {(score: number|null) => Promise<boolean>} props.onSave null は「AI の値に戻す」
 * @param {() => void} props.onClearError
 */
function RecommendationScore({
    item,
    scoreHidden = false,
    editing = false,
    saving = false,
    locked = false,
    errorMessage = null,
    onStartEdit,
    onCancelEdit,
    onSave,
    onClearError,
}) {
    const displayed = getDisplayedScore(item);
    const override = readScoreOverride(item);

    const [text, setText] = useState('');
    const [inputError, setInputError] = useState(null);
    const inputRef = useRef(null);
    const editButtonRef = useRef(null);
    const wasEditingRef = useRef(false);
    const focusEditAfterRevertRef = useRef(false);

    // 編集を始めたら、今表示している値を入力欄に入れてフォーカスを移す。
    // 編集を終えたら (保存できた・キャンセルした)、フォーカスを「編集」に戻す (§7.2)
    useEffect(() => {
        if (editing && !wasEditingRef.current) {
            setText(String(displayed));
            setInputError(null);
            inputRef.current?.focus();
            inputRef.current?.select();
        } else if (!editing && wasEditingRef.current) {
            editButtonRef.current?.focus();
        }
        wasEditingRef.current = editing;
        // displayed は編集を始めた時点の値だけを使う (編集中に変わっても入力欄を上書きしない)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    // 「AI の値に戻す」が保存できた後のフォーカスの移動。保存の結果を受け取った時点では、「編集」が
    // まだ押せない表示 (保存中) のままのことがあるため、押せる表示になった後の描画でも試す
    const focusEditButtonIfPending = () => {
        const button = editButtonRef.current;
        if (!focusEditAfterRevertRef.current || !button || button.disabled) return;
        focusEditAfterRevertRef.current = false;
        button.focus();
    };
    useEffect(focusEditButtonIfPending);

    if (scoreHidden) return null;

    const handleChange = (event) => {
        setText(event.target.value);
        setInputError(null);
        onClearError?.();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (saving) return;
        const parsed = parseScoreInput(text);
        if (parsed === null) {
            setInputError(SCORE_INPUT_ERROR_MESSAGE);
            return;
        }
        // 今表示している値と同じなら、保存を呼ばずに編集を終える (§6.2 手順 3)
        if (parsed === displayed) {
            onCancelEdit?.();
            return;
        }
        onSave?.(parsed);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !saving) {
            event.preventDefault();
            onCancelEdit?.();
        }
    };

    // AI の値に戻す (BTN-M-013)。保存できるとこのボタンは消えるため、フォーカスを「編集」に移す (§7.2)
    const handleRevert = async () => {
        const saved = await onSave?.(null);
        if (saved) {
            focusEditAfterRevertRef.current = true;
            focusEditButtonIfPending();
        }
    };

    const alertMessage = inputError || errorMessage;

    if (editing) {
        return (
            <form className="recommendation-score-area recommendation-score-area--editing" onSubmit={handleSubmit} noValidate>
                <span className="recommendation-score-edit-row">
                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        className="recommendation-score-input tabular-nums"
                        aria-label="マッチ度（0〜100）"
                        value={text}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        disabled={saving}
                    />
                    <span aria-hidden="true">%</span>
                    <button type="submit" className="btn-row btn-row--primary" disabled={saving}>
                        保存
                    </button>
                    <button type="button" className="btn-row btn-row--ghost" onClick={() => onCancelEdit?.()} disabled={saving}>
                        キャンセル
                    </button>
                    {/* 領域ごと出し入れすると読み上げられないことがあるため、role="status" は常に置く */}
                    <span role="status" className="recommendation-score-status">
                        {saving && '保存中...'}
                    </span>
                </span>
                {alertMessage && (
                    <span className="recommendation-score-error" role="alert">{alertMessage}</span>
                )}
            </form>
        );
    }

    return (
        <div className="recommendation-score-area">
            <span className="recommendation-score-edit-row">
                <span className="recommendation-score" aria-label={`マッチ度 ${displayed}%`}>
                    <span className="tabular-nums">{displayed}%</span>
                </span>
                {/* DP-M-014: 手で直したことを、色ではなく文字で示す (推薦 PDF には出さない) */}
                {override !== null && (
                    <span className="recommendation-score-manual">
                        <span className="recommendation-score-manual-badge">手入力</span>
                        <span className="tabular-nums">AI の値: {getAiScore(item)}%</span>
                    </span>
                )}
                {override !== null && (
                    <button
                        type="button"
                        className="btn-row btn-row--ghost"
                        onClick={handleRevert}
                        disabled={locked || saving}
                    >
                        AI の値に戻す
                    </button>
                )}
                <button
                    ref={editButtonRef}
                    type="button"
                    className="btn-row btn-row--ghost"
                    aria-label="マッチ度を編集"
                    onClick={() => onStartEdit?.()}
                    disabled={locked || saving}
                >
                    編集
                </button>
                <span role="status" className="recommendation-score-status">
                    {saving && '保存中...'}
                </span>
            </span>
            {errorMessage && (
                <span className="recommendation-score-error" role="alert">{errorMessage}</span>
            )}
        </div>
    );
}

export default RecommendationScore;
